import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { appointmentCancelMutation, appointmentDestroyMutation, appointmentIndexQueryKey, appointmentShowOptions, appointmentShowQueryKey, appointmentUpdateMutation } from '$api-client/@tanstack/react-query.gen'
import { PageLayout } from '$layouts/page'
import { Card, Text, Breadcrumb, Table, Image, LinkButton, Skeleton, Tooltip } from '$components'
import { DogIcon, LinkIcon, XIcon, CheckDoneIcon, EditIcon, TimeForwardIcon } from '$components'
import { formatDate } from '$components'
import { ElementIntent } from '$components'
import { InfoSkeleton } from '$components'
import { createColumnHelper } from '@tanstack/react-table'
import { GenericTable } from '$components'
import { AppointmentService, AppointmentStatus } from '$api-client/types.gen'
import { useMemo } from 'react'
import { AppointmentStatusPill } from '$components'
import dayjs from 'dayjs'
import { ImageCircle } from '$components'
import { AppointmentHelper } from '$components'
import { Router } from '$utils/router'
import { ServiceRoutePath } from '$features/services/routes/protected'
import { ClientRoutePath } from '$features/clients/routes/protected'
import { PetRoutePaths } from '$features/pets/routes/protected'
import { getCurrencySymbol } from '$utils/currency'
import { OptionsDropdown } from '$components'
import { ConfirmationModal } from '$components'
import { AnimatedOutlet } from '$components'
import { AppointmentRoutePath } from '../routes/protected'
import { useModalState } from '$components'
import { PendingBookingProfileBanner } from '$components'
import { useAuth } from '$features/auth/hooks/use-auth'
import { PermissionsHelper } from '$utils/permissions'
import { Role } from '$api-client/types.gen'
import { TableSkeleton } from '$components'
import { ProfileSkeleton } from '$components'
import { BreadcrumbSkeleton } from '$components'
import { useTenant } from '$features/auth/hooks/use-tenant'
import { SUBSCRIPTION_EXPIRED_MESSAGE } from '$constants/subscription'
import { ToastHelper } from '$components'
import { usePageTitle } from '$components'
import { SharedQueryParams } from 'src/enums/query'
import { QueryErrorHelper } from '$utils/query'
import { AppointmentsFilterParam } from '../enums/filter-params'
import { ClientsFilterParam } from '$features/clients/enums/filter-params'
import { PetsFilterParam } from '$features/pets/enums/filter-params'

const columnHelper = createColumnHelper<AppointmentService>()

export function AppointmentPage() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { hasSubscriptionExpired } = useTenant()
  const { modalState: cancelModalState, open: openCancelModal, close: closeCancelModal } = useModalState()
  const { modalState: rejectModalState, open: openRejectModal, close: closeRejectModal } = useModalState()

  const { data: appointment, isLoading, error } = useQuery({
    ...appointmentShowOptions({
      path: {
        id: Number(id),
      },
      query: {
        [AppointmentsFilterParam.IncludeUnapproved]: searchParams.get(AppointmentsFilterParam.IncludeUnapproved) === '1',
      },
    }),
  })

  usePageTitle(appointment?.data ? `Appointment - ${appointment.data.pet.name}` : 'Appointment')

  const { mutate: cancelAppointment, isPending: isCancellingAppointment } = useMutation({
    ...appointmentCancelMutation(),
    onSuccess: () => {
      closeCancelModal()
      ToastHelper.success('Appointment cancelled successfully.')
      queryClient.invalidateQueries({
        queryKey: appointmentIndexQueryKey(),
      })

      queryClient.invalidateQueries({
        queryKey: appointmentShowQueryKey({
          path: {
            id: Number(id),
          },
        }),
      })
    },
    onError: () => ToastHelper.error('Failed to cancel appointment.'),
  })

  const { mutate: rejectAppointment, isPending: isRejectingAppointment } = useMutation({
    ...appointmentDestroyMutation(),
    onSuccess: () => {
      closeRejectModal()
      ToastHelper.success('Appointment request rejected.')

      queryClient.invalidateQueries({
        queryKey: appointmentIndexQueryKey(),
      })

      navigate(Router.getRoute(AppointmentRoutePath.AppointmentsRequests))
    },
    onError: () => ToastHelper.error('Failed to reject appointment request.'),
  })

  const { mutate: updateAppointment } = useMutation({
    ...appointmentUpdateMutation(),
    onSuccess: () => {
      ToastHelper.success('Appointment status updated successfully.')
      queryClient.invalidateQueries({
        queryKey: appointmentIndexQueryKey(),
      })

      queryClient.invalidateQueries({
        queryKey: appointmentShowQueryKey({
          path: {
            id: Number(id),
          },
        }),
      })
    },
    onError: (error) => {
      const validation = QueryErrorHelper.getQueryErrorByCode(error, 'unavailable_slot')
      ToastHelper.error(validation ? validation.message : 'Failed to update appointment status.')
    },
  })

  const { mutate: approveAppointment } = useMutation({
    ...appointmentUpdateMutation(),
    onSuccess: () => {
      ToastHelper.success('Appointment approved.')

      queryClient.invalidateQueries({
        queryKey: appointmentIndexQueryKey(),
      })

      queryClient.invalidateQueries({
        queryKey: appointmentShowQueryKey({
          path: {
            id: Number(id),
          },
        }),
      })
    },
    onError: () => ToastHelper.error('Failed to approve appointment.'),
  })

  const handleConfirmCancel = () => {
    cancelAppointment({
      path: {
        id: cancelModalState.modelId!,
      },
    })
  }

  const handleConfirmReject = () => {
    rejectAppointment({
      path: {
        id: rejectModalState.modelId!,
      },
    })
  }

  const handleApproveAppointment = () => {
    approveAppointment({
      path: {
        id: Number(id),
      },
      body: {
        approved_at: dayjs().toISOString(),
      },
    })
  }

  const handleStatusChange = (newStatus: AppointmentStatus) => {
    updateAppointment({
      path: {
        id: Number(id),
      },
      body: {
        status: newStatus,
      },
    })
  }

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('service_values.name', {
        header: 'Service',
        cell: ({ row, getValue }) => (
          <div className="flex items-center gap-2">
            <div
              className="size-5 rounded-md"
              style={{ backgroundColor: row.original.service.colour }}
            />

            <Text type="s">{getValue()}</Text>
          </div>
        ),
      }),
      columnHelper.accessor('service_values.variants', {
        header: 'Price',
        cell: ({ getValue }) => {
          const price = getValue().find(variant => variant.is_default) ?? getValue()[0]

          return (
            <Text type="s">{getCurrencySymbol(appointment!.data.currency)}{price!.price_in_cents / 100}</Text>
          )
        },
      }),
    ]
  }, [appointment])

  if (isLoading) {
    return (
      <PageLayout>
        <div className="flex flex-col gap-6">
          {/* Breadcrumb Skeleton */}
          <BreadcrumbSkeleton />

          <div className="flex flex-col gap-4">
            {/* Header Skeleton */}
            <div className="flex flex-col gap-3">
              <div className="flex items-start justify-between">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-4 flex-wrap">
                    <Skeleton
                      width={200}
                      height={32}
                      borderRadius={8}
                    />
                    <Skeleton
                      width={100}
                      height={24}
                      borderRadius={12}
                    />
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <Skeleton
                      width={120}
                      height={16}
                      borderRadius={6}
                    />
                    <Skeleton
                      width={160}
                      height={16}
                      borderRadius={6}
                    />
                    <Skeleton
                      width={80}
                      height={16}
                      borderRadius={6}
                    />
                  </div>
                </div>

                <Skeleton
                  width={36}
                  height={36}
                  borderRadius={10}
                />
              </div>
            </div>

            <div className="flex flex-col gap-6">
              {/* Appointment Details Card Skeleton */}
              <Card
                rounding="xl"
                padding="none"
              >
                <Card.Header padding="sm">
                  <Skeleton
                    width={160}
                    height={20}
                    borderRadius={7}
                  />
                </Card.Header>

                <Card.Content
                  padding="sm"
                  className="flex flex-col gap-6"
                >
                  {/* Pet Information Skeleton */}
                  <ProfileSkeleton />

                  {/* Client Information Skeleton */}
                  <ProfileSkeleton />

                  {/* Notes Skeleton */}
                  <InfoSkeleton
                    labelWidth={50}
                    contentWidth={200}
                  />

                  <InfoSkeleton
                    labelWidth={50}
                    contentWidth={200}
                  />
                </Card.Content>
              </Card>

              {/* Services Section Skeleton */}
              <div className="flex flex-col gap-4">
                <Skeleton
                  width={80}
                  height={24}
                  borderRadius={6}
                />

                {/* Services Table Skeleton */}
                <TableSkeleton
                  columns={2}
                  rows={3}
                  columnWidths={[200, 100]}
                />

                {/* Total Skeleton */}
                <div className="flex justify-end">
                  <Skeleton
                    width={100}
                    height={20}
                    borderRadius={6}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (error || !appointment) {
    return (
      <PageLayout>
        <div className="flex flex-col items-center justify-center h-full">
          <Text
            as="h2"
            className="text-red-500"
          >Error loading appointment</Text>
          <Text>{error?.message || 'Appointment not found'}</Text>
        </div>
      </PageLayout>
    )
  }

  const appt = appointment.data

  return (
    <PageLayout>
      <div className="flex flex-col gap-6">
        <Breadcrumb
          items={[
            {
              path: AppointmentRoutePath.Appointments,
              label: 'Appointments',
              icon: CheckDoneIcon,
            },
            {
              path: Router.getRoute(AppointmentRoutePath.AppointmentsView, { id: appt.id }),
              label: `Appointment #${appt.id}`,
            },
          ]}
        />

        {!appt.approved_at && <PendingBookingProfileBanner />}

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-start justify-between">
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-4 flex-wrap">
                  <Text
                    as="h1"
                    className="text-2xl font-semibold"
                  >
                    Appointment #{appt.id}
                  </Text>
                  <AppointmentStatusPill
                    status={appt.status}
                    onStatusChange={handleStatusChange}
                  />
                </div>

                <div className="flex items-center gap-2 flex-wrap text-gray-600">
                  <Text type="s">
                    {formatDate(appt.start_at)}
                  </Text>
                  <Text type="s">
                    {dayjs.utc(appt.start_at).format('HH:mm A')} - {dayjs.utc(appt.finish_at).format('HH:mm A')}
                  </Text>
                  <Text type="s">
                    ({AppointmentHelper.calculateAppointmentDuration(appt.start_at, appt.finish_at)})
                  </Text>
                </div>
              </div>

              <OptionsDropdown options={!appt.approved_at
                ? [
                  {
                    id: 'approve',
                    value: 'Approve',
                    icon: CheckDoneIcon,
                    onClick: handleApproveAppointment,
                    disabled: PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN]),
                    tooltip: PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN])
                      ? 'You are not allowed to approve appointments.'
                      : undefined,
                  },
                  {
                    id: 'reject',
                    value: 'Reject',
                    intent: ElementIntent.Danger,
                    onClick: () => openRejectModal(appt.id),
                    icon: XIcon,
                  },
                ]
                : [
                  {
                    id: 'reschedule',
                    value: 'Reschedule',
                    onClick: () => navigate(Router.getRoute(AppointmentRoutePath.AppointmentsReschedule, { id: appt.id })),
                    icon: TimeForwardIcon,
                    disabled: [AppointmentStatus.COMPLETED, AppointmentStatus.CANCELLED, AppointmentStatus.NO_SHOW].includes(appt.status) || PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN]),
                    tooltip: [AppointmentStatus.COMPLETED, AppointmentStatus.CANCELLED, AppointmentStatus.NO_SHOW].includes(appt.status)
                      ? `Cannot reschedule ${appt.status.toLowerCase()} appointments.`
                      : PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN])
                        ? 'You are not allowed to reschedule appointments.'
                        : undefined,
                  },
                  {
                    id: 'cancel',
                    value: 'Cancel',
                    onClick: () => openCancelModal(appt.id),
                    intent: ElementIntent.Danger,
                    icon: XIcon,
                    disabled: appt.status === AppointmentStatus.CANCELLED || PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN]),
                    tooltip: appt.status === AppointmentStatus.CANCELLED
                      ? 'This appointment has already been cancelled.'
                      : PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN])
                        ? 'You are not allowed to cancel appointments.'
                        : undefined,
                  },
                ]}
              />
            </div>
          </div>

          <div className="flex flex-col gap-6">
            {/* Appointment Details */}
            <Card
              rounding="xl"
              padding="none"
            >
              <Card.Header padding="sm">
                <Text type="h4">Appointment Details</Text>
              </Card.Header>

              <Card.Content
                padding="sm"
                className="flex flex-col gap-6"
              >
                {/* Pet Information */}
                <div>
                  <Card.Label>Pet</Card.Label>

                  <Link
                    to={
                      Router.buildRoute(
                        Router.getRoute(PetRoutePaths.PetsView, { id: appt.pet.id }),
                        PetRoutePaths.PetsDetails,
                        !appt.pet.approved_at ? { [PetsFilterParam.IncludeUnapproved]: '1' } : undefined,
                      )
                    }
                  >
                    <div className="flex items-center gap-3 group">
                      <Image
                        size="sm"
                        src={appt.pet.image}
                        alt={appt.pet.name}
                        fallbackIcon={DogIcon}
                      />

                      <div>
                        <Text
                          type="h5"
                          className="group-hover:text-gray-900"
                        >
                          { appt.pet.name }
                        </Text>

                        <Text
                          type="s"
                          className="text-gray-500 group-hover:text-gray-800"
                        >
                          {appt.pet.breed.join(', ')}
                        </Text>
                      </div>

                      <LinkIcon className="size-4 group-hover:text-gray-900 text-gray-600" />
                    </div>
                  </Link>
                </div>

                {/* Client Information */}
                <div>
                  <Card.Label>Client</Card.Label>

                  <Link
                    to={
                      Router.buildRoute(
                        Router.getRoute(ClientRoutePath.ClientsView, { id: appt.client.id }),
                        ClientRoutePath.ClientsDetails,
                        !appt.client.approved_at ? { [ClientsFilterParam.IncludeUnapproved]: '1' } : undefined,
                      )
                    }
                  >
                    <div className="flex items-center gap-3 group">
                      <ImageCircle
                        size="lg"
                        image={appt.client.image}
                      />

                      <div>
                        <Text
                          type="h5"
                          className="group-hover:text-gray-900"
                        >
                          { `${appt.client.first_name} ${appt.client.last_name}` }
                        </Text>

                        <Text
                          type="s"
                          className="text-gray-500 group-hover:text-gray-800"
                        >
                          { appt.client.phone }
                        </Text>
                      </div>

                      <LinkIcon className="size-4 group-hover:text-gray-900 text-gray-600" />
                    </div>
                  </Link>
                </div>

                <div>
                  <Card.Label>Groomer</Card.Label>
                  <Text type="s">
                    { appt.groomer.first_name } { appt.groomer.last_name }
                  </Text>
                </div>

                <div>
                  <Card.Label>Grooming Preferences</Card.Label>
                  <Text type="s">
                    { appt.pet.grooming_preferences ?? 'No grooming preferences' }
                  </Text>
                </div>

                {/* Notes */}
                <div className="flex items-center gap-4">
                  <div>
                    <Card.Label>Notes</Card.Label>

                    <Text type="s">
                      { appt.notes ?? 'No notes' }
                    </Text>
                  </div>

                  <Tooltip
                    content={hasSubscriptionExpired ? SUBSCRIPTION_EXPIRED_MESSAGE : undefined}
                    disabled={!hasSubscriptionExpired}
                  >
                    <LinkButton
                      variant="secondary"
                      size="sm"
                      icon={EditIcon}
                      to={Router.getRoute(
                        AppointmentRoutePath.AppointmentsNotesEdit,
                        { id: appt.id },
                        !appt.approved_at ? { [AppointmentsFilterParam.IncludeUnapproved]: '1' } : undefined,
                      )}
                      disabled={hasSubscriptionExpired}
                    >
                      Edit
                    </LinkButton>
                  </Tooltip>
                </div>
              </Card.Content>
            </Card>

            {/* Services */}
            <div className="flex flex-col gap-4 mb-4">
              <Text type="h3">Services</Text>

              <Table.Container>
                <GenericTable
                  data={appt.services}
                  columns={columns}
                  rowLink={(row) => {
                    const baseUrl = Router.getRoute(ServiceRoutePath.ServicesView, { id: row.service.id })
                    const isOutdated = !row.service_values.is_latest
                    if (isOutdated) {
                      return `${baseUrl}?${SharedQueryParams.ValuesId}=${row.service_values.id}`
                    }
                    return baseUrl
                  }}
                />

                <div className="flex justify-end pr-4">
                  <Text
                    type="s"
                    weight="medium"
                  >
                    Total: {getCurrencySymbol(appt.currency)}{appt.price_in_cents / 100}
                  </Text>
                </div>
              </Table.Container>
            </div>
          </div>
        </div>
      </div>

      { cancelModalState.isOpen && (
        <ConfirmationModal
          onClose={closeCancelModal}
          title="Cancel Appointment"
          description="Are you sure you want to cancel this appointment?"
          intent={ElementIntent.Danger}
          isLoading={isCancellingAppointment}
          onConfirm={handleConfirmCancel}
        />
      )}

      { rejectModalState.isOpen && (
        <ConfirmationModal
          onClose={closeRejectModal}
          title="Reject Appointment Request"
          description="Are you sure you want to reject this appointment request?"
          intent={ElementIntent.Danger}
          isLoading={isRejectingAppointment}
          onConfirm={handleConfirmReject}
        />
      )}

      <AnimatedOutlet />
    </PageLayout>
  )
}
